import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';

import { IconsModule } from '../../icons/icons.module';

/** Identity for deduplication: browsers expose no stable file id. */
function fileKey(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

/**
 * What document-store stores, mirroring `ServiceConstants.ALLOWED_FILE`.
 *
 * Keep in sync with document-store: it rejects anything else on upload, and a
 * file refused here never costs the agent a round trip to find that out.
 */
const STORABLE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'pdf',
  'doc',
  'docx',
];

/** `contrat.pdf` -> `pdf`; empty when the name carries no extension. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot < 0 ? '' : fileName.slice(dot + 1).toLowerCase();
}

/**
 * The extensions an `accept` string allows, or null for "no opinion".
 *
 * Only the extension forms are read (`.pdf`, `pdf`); a media-type entry such as
 * `image/*` is dropped, since matching it needs the browser's own table. An
 * accept that named nothing else then constrains nothing, and the storable list
 * below is left to do the work.
 */
function acceptedExtensions(accept: string): string[] | null {
  const extensions = accept
    .split(',')
    .map((entry) => entry.trim().replace(/^\./, '').toLowerCase())
    .filter((entry) => !!entry && entry !== '*' && !entry.includes('/'));
  return extensions.length ? extensions : null;
}

@Component({
  selector: 'app-file-input',
  templateUrl: './file-input.component.html',
  standalone: true,
  imports: [CommonModule, FileSizePipe, IconsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FileInputComponent),
    },
  ],
})
export class FileInputComponent implements ControlValueAccessor {
  accept = input('*');

  /**
   * Opt-in list mode: the picker accepts several files, every pick is appended
   * to the current selection, and the control value is a `File[]` instead of
   * the default `File | null`.
   */
  multiple = input(false);

  /**
   * Put on the native input, so a caller's <label for=…> can point at it. Without one the label
   * belongs to nothing, and a screen reader announces an unnamed file control.
   */
  inputId = input<string | undefined>(undefined);

  fileChange = output<File | File[] | null>();

  files = signal<File[]>([]);

  /**
   * Names of the files the last pick refused, for the message under the field.
   * Empty whenever everything picked was kept.
   */
  rejectedNames = signal<string[]>([]);

  disabled = signal(false);

  private nativeInput =
    viewChild.required<ElementRef<HTMLInputElement>>('nativeInput');

  private onChange?: (value: File | File[] | null) => void;

  private onTouched?: () => void;

  onFileUpload(event: Event) {
    const picked = this.keepAllowed(
      Array.from((event.target as HTMLInputElement).files ?? [])
    );
    if (this.multiple()) {
      // Appended rather than replaced, so files can be added over several
      // picks — but a file already in the list is not added twice. The native
      // value is cleared so re-picking a file after its removal still fires.
      const known = new Set(this.files().map(fileKey));
      this.files.set([
        ...this.files(),
        ...picked.filter((file) => !known.has(fileKey(file))),
      ]);
      this.nativeInput().nativeElement.value = '';
    } else if (picked.length) {
      this.files.set(picked.slice(0, 1));
    } else {
      // Every file was refused: the selection the agent already had is worth
      // more than the one they just mispicked, so it stands. The native value
      // is cleared so it no longer names a file that was never taken.
      this.nativeInput().nativeElement.value = '';
    }
    this.propagate();
  }

  /**
   * Drops what neither the field nor document-store would take, and remembers
   * the names so the template can say which.
   *
   * Both lists apply: `accept` is what this field asked for, and the storable
   * list is what the backend will keep — a file passing here can never be
   * rejected on upload for its type. `accept` is otherwise only a hint to the
   * file picker, which "All files" and drag-and-drop both walk straight past.
   */
  private keepAllowed(picked: File[]): File[] {
    const accepted = acceptedExtensions(this.accept());
    const allowed = picked.filter((file) => {
      const extension = extensionOf(file.name);
      return (
        STORABLE_EXTENSIONS.includes(extension) &&
        (!accepted || accepted.includes(extension))
      );
    });

    this.rejectedNames.set(
      picked.filter((file) => !allowed.includes(file)).map((file) => file.name)
    );
    return allowed;
  }

  removeFileAt(index: number) {
    this.files.set(this.files().filter((_, i) => i !== index));
    if (!this.files().length) {
      this.nativeInput().nativeElement.value = '';
    }
    this.propagate();
  }

  writeValue(value: File | File[] | null): void {
    const files = value == null ? [] : Array.isArray(value) ? value : [value];
    this.rejectedNames.set([]);
    this.files.set(files);
    if (!files.length) {
      this.nativeInput().nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: File | File[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private propagate() {
    const value = this.multiple() ? this.files() : (this.files()[0] ?? null);
    this.fileChange.emit(value);
    this.onChange?.(value);
    this.onTouched?.();
  }
}
